import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useEffect, useState } from "react";
import { api, formatApiError, ron } from "@/lib/api";
import DateInput from "@/components/DateInput";
import DualMoneyInput from "@/components/DualMoneyInput";
import { toast } from "sonner";

// Surse predefinite de unde clientul a văzut mașina
export const SALE_SOURCES = [
  "Autovit",
  "OLX",
  "Google",
  "Facebook",
  "Instagram",
  "Site propriu",
  "Recomandare",
  "Direct (showroom)",
  "Altele",
];

const EMPTY_META = { original_amount: null, original_currency: null, rate_at_entry: null, rate_locked: false };

export default function MarkSoldDialog({ open, onClose, car, onSold }) {
  const [clients, setClients] = useState([]);
  const [form, setForm] = useState({ sale_price: "", client_id: "", notes: "", sale_date: "", source: "", ...EMPTY_META });
  const [inputKey, setInputKey] = useState(0);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open) return;
    setForm({ sale_price: car.asking_price || "", client_id: "", notes: "", sale_date: new Date().toISOString().slice(0, 10), source: "", ...EMPTY_META });
    setInputKey((k) => k + 1);
    api.get("/clients").then((r) => setClients(r.data)).catch(() => {});
  }, [open, car]);

  // Pre-fill source from client.source when client is selected (only if currently empty + matches predefined list)
  useEffect(() => {
    if (!form.client_id || form.source) return;
    const c = clients.find((x) => x.id === form.client_id);
    if (c && c.source && SALE_SOURCES.includes(c.source)) {
      setForm((f) => ({ ...f, source: c.source }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.client_id, clients]);

  async function submit() {
    setLoading(true);
    try {
      await api.post("/sales", {
        car_id: car.id,
        client_id: form.client_id || null,
        sale_price: Number(form.sale_price) || 0,
        sale_date: form.sale_date ? new Date(form.sale_date).toISOString() : null,
        notes: form.notes,
        source: form.source || null,
        original_amount: form.original_amount,
        original_currency: form.original_currency,
        rate_at_entry: form.rate_at_entry,
        rate_locked: !!form.rate_locked,
      });
      if (form.client_id) {
        toast.success("Vânzare înregistrată · follow-up programat la 7 zile 📞");
      } else {
        toast.success("Vânzare înregistrată");
      }
      onSold?.();
      onClose();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Eroare");
    } finally {
      setLoading(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent data-testid="mark-sold-dialog" className="max-w-md">
        <DialogHeader>
          <DialogTitle className="font-display tracking-tight">Marchează ca vândut</DialogTitle>
        </DialogHeader>
        <div className="text-sm text-slate-500">
          {car.make} {car.model} · investiție <strong className="tabular text-slate-900">{ron(car.investment_total)}</strong>
        </div>
        <div className="space-y-3">
          <div>
            <Label>Preț vânzare</Label>
            <DualMoneyInput
              key={inputKey}
              testid="sold-price"
              value={form.sale_price}
              onChange={(v) => setForm((f) => ({ ...f, sale_price: v }))}
              onMeta={(m) => setForm((f) => ({ ...f, ...m }))}
            />
            {Number(form.sale_price) > 0 && (
              <div className="text-xs mt-1">
                Profit: <span className={`font-semibold tabular ${Number(form.sale_price) - car.investment_total >= 0 ? "text-emerald-600" : "text-red-600"}`}>
                  {ron(Number(form.sale_price) - car.investment_total)}
                </span>
              </div>
            )}
          </div>
          <div>
            <Label>Data vânzării</Label>
            <DateInput testid="sold-date" value={form.sale_date} onChange={(v) => setForm({ ...form, sale_date: v })} />
          </div>
          <div>
            <Label>Client</Label>
            <Select value={form.client_id} onValueChange={(v) => setForm({ ...form, client_id: v })}>
              <SelectTrigger data-testid="sold-client"><SelectValue placeholder="— alege clientul —" /></SelectTrigger>
              <SelectContent>
                {clients.map((c) => (<SelectItem key={c.id} value={c.id}>{c.name} · {c.phone}</SelectItem>))}
              </SelectContent>
            </Select>
            {form.client_id ? (
              <p className="text-xs text-emerald-700 mt-1.5 flex items-start gap-1.5">
                <span>📞</span>
                <span>Se programează automat un follow-up în calendar peste 7 zile pentru a întreba clientul cum este mașina și a-i cere o recenzie.</span>
              </p>
            ) : (
              <p className="text-xs text-amber-700 mt-1.5 flex items-start gap-1.5">
                <span>⚠️</span>
                <span>Fără client selectat nu se va programa follow-up automat la 7 zile.</span>
              </p>
            )}
          </div>
          <div>
            <Label>De unde a văzut mașina</Label>
            <Select value={form.source} onValueChange={(v) => setForm({ ...form, source: v })}>
              <SelectTrigger data-testid="sold-source"><SelectValue placeholder="— alege platforma —" /></SelectTrigger>
              <SelectContent>
                {SALE_SOURCES.map((s) => (<SelectItem key={s} value={s}>{s}</SelectItem>))}
              </SelectContent>
            </Select>
            <p className="text-xs text-slate-500 mt-1.5">
              Folosit pentru raport „Vânzări pe platformă" pe Dashboard.
            </p>
          </div>
          <div>
            <Label>Notițe</Label>
            <Textarea data-testid="sold-notes" rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Anulează</Button>
          <Button data-testid="sold-submit" onClick={submit} disabled={loading} className="bg-emerald-600 hover:bg-emerald-700">
            {loading ? "Se salvează..." : "Confirmă vânzarea"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
